import { formatDateTime, formatDate } from "features/discover/functions";
import type { ColumnConfig, FilterConfig } from "../../types";
import { BooleanChoices } from "features/discover/types";
import { renderAvgDays, renderNumber, renderPercent } from "../../cellRenderers";
import { TooltipComponent } from "ui-kit/src/TooltipComponent/TooltipComponent";

/**
 * Type for a single row in the Countries tab.
 * Aggregated data per country.
 */
export type CountriesRow = Record<string, unknown>;

/**
 * Column definitions for Countries tab.
 * Shows columns where "ALL = Yes" in the spec.
 * Displays aggregated data per Country.
 */
export const countriesColumns: ColumnConfig<CountriesRow>[] = [
	// ========== Overview Group ==========
	{
		key: "continent",
		label: "Continent",
		sortable: true,
		sortField: "continent",
		cell: (value) => (value as string) || "-",
		group: "Overview",
		defaultVisible: true,
		requiredFields: ["continent"],
		width: 100,
	},
	{
		key: "country",
		label: "Country",
		sortable: true,
		sortField: "countryName",
		cell: (_value, row) => (row?.countryName as string) || (row?.country as string) || "-",
		group: "Overview",
		defaultVisible: true,
		requiredFields: ["country", "countryName"],
		width: 130,
	},
	{
		key: "status",
		label: "Status / Opened",
		sortable: true,
		sortField: "status",
		cell: (value) => {
			if (value === null || value === undefined) return "-";
			return value === 1 || value === true ? "Yes" : "No";
		},
		group: "Overview",
		defaultVisible: true,
		requiredFields: ["status"],
		width: 120,
	},

	// ========== Partner Club Stats Group ==========
	{
		key: "totalPartnerClubs",
		label: "Total Partner Clubs",
		sortable: true,
		sortField: "totalPartnerClubs",
		cell: (value) => renderNumber(value as number | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["totalPartnerClubs"],
		summable: true, // Sum of Rows
		width: 160,
	},
	{
		key: "targetPartnerClubs",
		label: "Target Partner Clubs",
		sortable: true,
		sortField: "targetPartnerClubs",
		cell: (value) => renderNumber(value as number | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["targetPartnerClubs"],
		summable: true, // Sum of Rows
		width: 160,
	},
	{
		key: "numberOfScoutTeams",
		label: "Number of Scout Teams",
		sortable: true,
		sortField: "numberOfScoutTeams",
		cell: (value) => renderNumber(value as number | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["numberOfScoutTeams"],
		summable: true, // Sum of Rows
		width: 180,
	},
	{
		key: "numberOfDiscoverTeams",
		label: "Number of Discover Teams",
		sortable: true,
		sortField: "numberOfDiscoverTeams",
		cell: (value) => renderNumber(value as number | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["numberOfDiscoverTeams"],
		summable: true, // Sum of Rows
		width: 190,
	},
	{
		key: "consistentRunningPlayerPercent",
		label: "Consistent Running Player Percent",
		sortable: true,
		sortField: "consistentRunningPlayerPercent",
		cell: (value) => renderPercent(value as number | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["consistentRunningPlayerPercent"],
		// Summary: Avg of Rows (not summable, needs averaging)
		width: 200,
	},
	{
		key: "variablePlayerQualityPercent",
		label: "Variable Player Quality Percent",
		sortable: true,
		sortField: "variablePlayerQualityPercent",
		cell: (value) => renderPercent(value as number | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["variablePlayerQualityPercent"],
		// Summary: Avg of Rows (not summable, needs averaging)
		width: 200,
	},
	{
		key: "ntPlayers",
		label: "NT Players",
		sortable: true,
		sortField: "ntPlayers",
		cell: (value) => renderNumber(value as number | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["ntPlayers"],
		summable: true, // Sum of Rows
		width: 110,
	},
	{
		key: "suggestedGamesUpdate",
		label: "Suggested Games Update",
		sortable: true,
		sortField: "suggestedGamesUpdate",
		cell: (value) => formatDateTime(value as string | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["suggestedGamesUpdate"],
		width: 200,
	},
	{
		key: "playerToWatchUpdate",
		label: "Player to Watch Update",
		sortable: true,
		sortField: "playerToWatchUpdate",
		cell: (value) => formatDateTime(value as string | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["playerToWatchUpdate"],
		width: 200,
	},
	{
		key: "seasonStart",
		label: "Season Start",
		sortable: true,
		sortField: "seasonStart",
		cell: (value) => formatDate(value as string | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["seasonStart"],
		width: 130,
	},
	{
		key: "seasonEnd",
		label: "Season End",
		sortable: true,
		sortField: "seasonEnd",
		cell: (value) => formatDate(value as string | null),
		group: "Partner Club Stats",
		defaultVisible: true,
		requiredFields: ["seasonEnd"],
		width: 130,
	},

	// ========== Game Volumes Group ==========
	{
		key: "totalGames",
		label: "Total Games",
		sortable: true,
		sortField: "totalGames",
		cell: (value) => renderNumber(value as number | null),
		group: "Game Volumes",
		defaultVisible: true,
		requiredFields: ["totalGames"],
		summable: true, // Sum of Rows
		width: 130,
	},
	{
		key: "gamesTagged",
		label: "Games Tagged",
		sortable: true,
		sortField: "gamesTagged",
		cell: (value) => renderNumber(value as number | null),
		group: "Game Volumes",
		defaultVisible: true,
		requiredFields: ["gamesTagged"],
		summable: true, // Sum of Rows
		width: 130,
	},
	{
		key: "percentOfGamesTagged",
		label: "% of Games Tagged",
		sortable: true,
		sortField: "percentOfGamesTagged",
		cell: (value) => renderPercent(value as number | null),
		group: "Game Volumes",
		defaultVisible: true,
		requiredFields: ["percentOfGamesTagged"],
		// Summary: Avg of Rows (not summable, needs averaging)
		width: 150,
	},
	{
		key: "avgGamePriority",
		label: "Avg Game Priority",
		sortable: true,
		sortField: "avgGamePriority",
		cell: (value) => {
			if (value == null) return "-";
			return (value as number).toFixed(2);
		},
		group: "Game Volumes",
		defaultVisible: true,
		requiredFields: ["avgGamePriority"],
		// Summary: Avg of Rows (not summable, needs averaging)
		width: 150,
	},
	{
		key: "avgGameAnalysisProcessing",
		label: "Avg Game Analysis Processing",
		sortable: true,
		sortField: "avgGameAnalysisProcessing",
		cell: (value) => renderAvgDays(value as number | null),
		group: "Game Volumes",
		defaultVisible: true,
		requiredFields: ["avgGameAnalysisProcessing"],
		// Summary: Avg of Rows (not summable, needs averaging)
		width: 220,
	},

	// ========== Market Value Group ==========
	{
		key: "viewsOfGames",
		label: "Views of Games",
		sortable: true,
		sortField: "viewsOfGames",
		cell: (value) => renderNumber(value as number | null),
		group: "Market Value",
		defaultVisible: true,
		requiredFields: ["viewsOfGames"],
		summable: true, // Sum of Rows
		width: 140,
	},
	{
		key: "proportionOfGameViews",
		label: "% of Game Views",
		sortable: true,
		sortField: "proportionOfGameViews",
		cell: (value) => renderPercent(value as number | null),
		group: "Market Value",
		defaultVisible: true,
		requiredFields: ["proportionOfGameViews"],
		// Summary: Avg of Rows (not summable, needs averaging)
		width: 190,
	},
	{
		key: "subscriptionsSold",
		label: "Active subscription",
		sortable: true,
		sortField: "subscriptionsSold",
		cell: (value, row) => {
			const num = renderNumber(value as number | null);
			const b = row?.bronzeSold as number | undefined;
			const s = row?.silverSold as number | undefined;
			const g = row?.goldSold as number | undefined;
			const p = row?.platinumSold as number | undefined;
			const o = row?.otherSold as number | undefined;
			const hasBreakdown = b !== undefined || s !== undefined || g !== undefined || p !== undefined || o !== undefined;
			const tooltipText = hasBreakdown
				? `Bronze: ${b ?? 0}, Silver: ${s ?? 0}, Gold: ${g ?? 0}, Platinum: ${p ?? 0}, Other: ${o ?? 0}`
				: "Breakdown not available";
			return (
				<div className="operationsManagementSummaryTableV3-subscriptionsSoldTrigger">
					<TooltipComponent title={tooltipText} delay={500}>
						{num}
					</TooltipComponent>
				</div>
			);
		},
		group: "Market Value",
		defaultVisible: true,
		requiredFields: ["subscriptionsSold", "bronzeSold", "silverSold", "goldSold", "platinumSold", "otherSold"],
		summable: true, // Sum of Rows
		width: 160,
	},
];

/**
 * Base endpoint for filter options.
 */
const FILTER_OPTIONS_ENDPOINT = "/admin/operations-management/summary/by-country/filter-options";

/**
 * Filter definitions for Countries tab.
 * Ordered to match column arrangement.
 */
export const countriesFilters: FilterConfig[] = [
	// ========== Overview Group ==========
	{
		id: "continent",
		label: "Continent",
		type: "select",
		field: "continent",
		filterType: "exact",
		options: [],
		optionsEndpoint: {
			url: FILTER_OPTIONS_ENDPOINT,
			filterParam: "filter",
		},
		placeholder: "Select continent...",
	},
	{
		id: "country",
		label: "Country",
		type: "multiselect",
		field: "country",
		filterType: "inarray",
		options: [],
		optionsEndpoint: {
			url: FILTER_OPTIONS_ENDPOINT,
			filterParam: "filter",
		},
		placeholder: "Select countries...",
	},
	{
		id: "status",
		label: "Status / Opened",
		type: "boolean",
		field: "status",
		filterType: "boolean",
		options: BooleanChoices,
	},

	// ========== Partner Club Stats Group ==========
	{
		id: "totalPartnerClubs",
		label: "Total Partner Clubs",
		type: "numberrange",
		field: "totalPartnerClubs",
		filterType: "numberrange",
		placeholder: "Total Partner Clubs...",
	},
	{
		id: "targetPartnerClubs",
		label: "Target Partner Clubs",
		type: "numberrange",
		field: "targetPartnerClubs",
		filterType: "numberrange",
		placeholder: "Target Partner Clubs...",
	},
	{
		id: "numberOfScoutTeams",
		label: "Number of Scout Teams",
		type: "numberrange",
		field: "numberOfScoutTeams",
		filterType: "numberrange",
		placeholder: "Number of Scout Teams...",
	},
	{
		id: "numberOfDiscoverTeams",
		label: "Number of Discover Teams",
		type: "numberrange",
		field: "numberOfDiscoverTeams",
		filterType: "numberrange",
		placeholder: "Number of Discover Teams...",
	},
	{
		id: "consistentRunningPlayerPercent",
		label: "Consistent Running Player %",
		type: "numberrange",
		field: "consistentRunningPlayerPercent",
		filterType: "numberrange",
		placeholder: "0 - 100 %",
	},
	{
		id: "variablePlayerQualityPercent",
		label: "Variable Player Quality %",
		type: "numberrange",
		field: "variablePlayerQualityPercent",
		filterType: "numberrange",
		placeholder: "0 - 100 %",
	},
	{
		id: "ntPlayers",
		label: "NT Players",
		type: "numberrange",
		field: "ntPlayers",
		filterType: "numberrange",
		placeholder: "NT Players...",
	},
	{
		id: "suggestedGamesUpdate",
		label: "Suggested Games Update",
		type: "daterange",
		field: "suggestedGamesUpdate",
		filterType: "datetimerange",
	},
	{
		id: "playerToWatchUpdate",
		label: "Player to Watch Update",
		type: "daterange",
		field: "playerToWatchUpdate",
		filterType: "datetimerange",
	},
	{
		id: "seasonStart",
		label: "Season Start",
		type: "daterange",
		field: "seasonStart",
		filterType: "daterange",
	},
	{
		id: "seasonEnd",
		label: "Season End",
		type: "daterange",
		field: "seasonEnd",
		filterType: "daterange",
	},

	// ========== Game Volumes Group ==========
	{
		id: "totalGames",
		label: "Total Games",
		type: "numberrange",
		field: "totalGames",
		filterType: "numberrange",
		placeholder: "Total Games...",
	},
	{
		id: "gamesTagged",
		label: "Games Tagged",
		type: "numberrange",
		field: "gamesTagged",
		filterType: "numberrange",
		placeholder: "Games Tagged...",
	},
	{
		id: "percentOfGamesTagged",
		label: "% of Games Tagged",
		type: "numberrange",
		field: "percentOfGamesTagged",
		filterType: "numberrange",
		placeholder: "0 - 100 %",
	},
	{
		id: "avgGamePriority",
		label: "Avg Game Priority",
		type: "numberrange",
		field: "avgGamePriority",
		filterType: "numberrange",
		placeholder: "Avg Game Priority...",
	},
	{
		id: "avgGameAnalysisProcessing",
		label: "Avg Game Analysis Processing",
		type: "numberrange",
		field: "avgGameAnalysisProcessing",
		filterType: "numberrange",
		placeholder: "Days...",
	},

	// ========== Market Value Group ==========
	{
		id: "viewsOfGames",
		label: "Views of Games",
		type: "numberrange",
		field: "viewsOfGames",
		filterType: "numberrange",
		placeholder: "Views of Games...",
	},
	{
		id: "proportionOfGameViews",
		label: "% of Game Views",
		type: "numberrange",
		field: "proportionOfGameViews",
		filterType: "numberrange",
		placeholder: "0 - 100 %",
	},
	{
		id: "subscriptionsSold",
		label: "Active subscription",
		type: "numberrange",
		field: "subscriptionsSold",
		filterType: "numberrange",
		placeholder: "Active subscription…",
	},
];
